import React, { useRef, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import withReducer from '@Hooks/withReducer';
import StructureEditorType from '@Containers/StructureEditor/constants';
import StructureEditor from '@Containers/StructureEditor';
import EditorControls from './Components/EditorControls';
import { REDUCER_KEY_STRUCTURE_EDITOR, STRUCTURE_INSERT_MODE_IMPORT, STRUCTURE_INSERT_MODE_LOAD } from './constants';
import reducer from './reducer';
import {
  structure as structureSelector,
  structureInsertMode as structureInsertModeSelector,
  changeStructureTriggerUid as changeStructureTriggerUidSelector,
} from './selectors';
import {
  initialize as initializeAction,
  onEditorChange as onEditorChangeAction,
  transfer as transferAction,
  cancel as cancelAction,
  clearStructure as clearStructureAction,
  clearState as clearStateAction,
} from './actions';
import { trackButton as trackButtonAnalytics } from './analytics';
import { importStructure, loadStructure, getEditorData, clearEditor } from './editorRefHelpers';

export function StructureEditorContainer({
  editorExportTarget,
  structureUid,
  context,
  keyForStructureData,
  onControlsReady,
  currentEditor,
  structure,
  structureInsertMode,
  changeStructureTriggerUid,
  initialize,
  onEditorChange,
  transfer,
  cancel,
  clearStructure,
  clearState,
}) {
  const editorRef = useRef();
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [editorType, setEditorType] = useState(currentEditor);

  // Initialization effect
  useEffect(() => {
    initialize({ structureUid });
    return () => {
      clearState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Structure effect
  useEffect(() => {
    if (editorLoaded && structure) {
      if (structureInsertMode === STRUCTURE_INSERT_MODE_LOAD) {
        loadStructure(editorRef, structure);
      } else {
        importStructure(editorRef, structure);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorLoaded, changeStructureTriggerUid]);

  // Editor change effect
  useEffect(() => {
    const prevEditor = editorType;
    const nextEditor = currentEditor;

    if (prevEditor !== nextEditor) {
      setEditorLoaded(false);
      getEditorData(editorRef)
        .then((data) => {
          onEditorChange({ prevEditor, nextEditor, structure: get(data, 'data') });
          setEditorType(currentEditor);
        })
        .catch(Logger.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditor]);

  const editorOnLoaded = useCallback(() => {
    setEditorLoaded(true);
  }, []);

  const onClear = useCallback(() => {
    // AA
    trackButtonAnalytics('clear');
    clearEditor(editorRef);
    clearStructure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCancel = useCallback(() => {
    // AA
    trackButtonAnalytics('cancel');
    cancel({ editorExportTarget });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTransfer = useCallback(() => {
    // AA
    trackButtonAnalytics('transfer to query');
    getEditorData(editorRef).then((data) => {
      if (data) {
        transfer({ data, structureUid, editorExportTarget, context, keyForStructureData });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transferButtonLabel = `Transfer to ${editorExportTarget !== 'results' ? 'query' : 'filter'}`;

  useEffect(() => {
    if (!onControlsReady) return;

    onControlsReady({
      onClear,
      onCancel,
      onTransfer,
      transferButtonDisabled: false,
      transferButtonLabel,
    });

    return () => {
      onControlsReady(null);
    };
  }, [onControlsReady, onClear, onCancel, onTransfer, editorExportTarget, transferButtonLabel]);

  return (
    <div className="structure-editor">
      <StructureEditor editorType={editorType} editorInstanceRef={editorRef} className="editor-wrapper" onLoaded={editorOnLoaded} />
      {!onControlsReady && (
        <EditorControls
          onClear={onClear}
          onCancel={onCancel}
          onTransfer={onTransfer}
          transferButtonDisabled={false}
          transferButtonLabel={transferButtonLabel}
        />
      )}
    </div>
  );
}

StructureEditorContainer.propTypes = {
  // From props
  editorExportTarget: PropTypes.string,
  structureUid: PropTypes.string,
  context: PropTypes.string,
  keyForStructureData: PropTypes.string,
  onControlsReady: PropTypes.func,
  currentEditor: PropTypes.oneOf(StructureEditorType.allTypes),
  structure: PropTypes.string,
  structureInsertMode: PropTypes.oneOf([STRUCTURE_INSERT_MODE_IMPORT, STRUCTURE_INSERT_MODE_LOAD]),
  changeStructureTriggerUid: PropTypes.string,
  initialize: PropTypes.func.isRequired,
  onEditorChange: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
  transfer: PropTypes.func.isRequired,
  clearStructure: PropTypes.func.isRequired,
  clearState: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  structure: structureSelector(state),
  structureInsertMode: structureInsertModeSelector(state),
  changeStructureTriggerUid: changeStructureTriggerUidSelector(state),
});

const mapDispatchToProps = {
  initialize: initializeAction,
  onEditorChange: onEditorChangeAction,
  transfer: transferAction,
  cancel: cancelAction,
  clearStructure: clearStructureAction,
  clearState: clearStateAction,
};

export default compose(
  withReducer(REDUCER_KEY_STRUCTURE_EDITOR, reducer),
  connect(mapStateToProps, mapDispatchToProps)
)(StructureEditorContainer);
